import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorMessage } from '../../components/ErrorMessage';
import { formatCurrency } from '../../utils/currency';
import { API_URL } from '../../api/client';

interface PortfolioMetrics {
  loans: {
    total_loans: string;
    active_loans: string;
    paid_off_loans: string;
    defaulted_loans: string;
    total_disbursed: string;
    total_outstanding: string;
    total_collected: string;
    default_rate: number;
  };
  applications: {
    total_applications: string;
    pending_review: string;
    under_review: string;
    approved: string;
    declined: string;
    approval_rate: number;
  };
  customers: {
    total_customers: string;
    active_customers: string;
    new_customers_30d: string;
  };
}

interface LoanTrend {
  month: string;
  loan_count: string;
  total_amount: string;
  avg_amount: string;
}

interface LoanStatus {
  status: string;
  count: string;
  total_amount: string;
  [key: string]: any;
}

export const Reports: React.FC = () => {
  const [portfolioMetrics, setPortfolioMetrics] = useState<PortfolioMetrics | null>(null);
  const [loanTrends, setLoanTrends] = useState<LoanTrend[]>([]);
  const [loanStatus, setLoanStatus] = useState<LoanStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');

      // Fetch all analytics data in parallel
      const [portfolioRes, trendsRes, statusRes] = await Promise.all([
        fetch(`${API_URL}/admin/analytics/portfolio`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${API_URL}/admin/analytics/loan-trends`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${API_URL}/admin/analytics/loan-status`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
      ]);

      if (!portfolioRes.ok || !trendsRes.ok || !statusRes.ok) {
        throw new Error('Failed to fetch analytics data');
      }

      const [portfolioData, trendsData, statusData] = await Promise.all([
        portfolioRes.json(),
        trendsRes.json(),
        statusRes.json(),
      ]);

      setPortfolioMetrics(portfolioData.data);
      setLoanTrends(trendsData.data);
      setLoanStatus(statusData.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const COLORS = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Reports & Analytics</h1>
        <p className="page-description">
          Portfolio performance and business insights
        </p>
      </div>

      {portfolioMetrics && (
        <>
          {/* Key Metrics Cards */}
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-icon" style={{ backgroundColor: '#dbeafe' }}>
                <span style={{ color: '#2563eb', fontSize: '24px' }}>💰</span>
              </div>
              <div className="metric-content">
                <h3 className="metric-label">Total Disbursed</h3>
                <p className="metric-value">{formatCurrency(portfolioMetrics.loans.total_disbursed)}</p>
                <span className="metric-detail">{portfolioMetrics.loans.total_loans} loans</span>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon" style={{ backgroundColor: '#d1fae5' }}>
                <span style={{ color: '#10b981', fontSize: '24px' }}>📊</span>
              </div>
              <div className="metric-content">
                <h3 className="metric-label">Total Collected</h3>
                <p className="metric-value">{formatCurrency(portfolioMetrics.loans.total_collected)}</p>
                <span className="metric-detail success">{portfolioMetrics.loans.paid_off_loans} paid off</span>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon" style={{ backgroundColor: '#fef3c7' }}>
                <span style={{ color: '#f59e0b', fontSize: '24px' }}>⏳</span>
              </div>
              <div className="metric-content">
                <h3 className="metric-label">Outstanding</h3>
                <p className="metric-value">{formatCurrency(portfolioMetrics.loans.total_outstanding)}</p>
                <span className="metric-detail warning">{portfolioMetrics.loans.active_loans} active</span>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon" style={{ backgroundColor: '#fee2e2' }}>
                <span style={{ color: '#ef4444', fontSize: '24px' }}>⚠️</span>
              </div>
              <div className="metric-content">
                <h3 className="metric-label">Default Rate</h3>
                <p className="metric-value">{portfolioMetrics.loans.default_rate.toFixed(2)}%</p>
                <span className="metric-detail error">{portfolioMetrics.loans.defaulted_loans} defaulted</span>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon" style={{ backgroundColor: '#e0e7ff' }}>
                <span style={{ color: '#6366f1', fontSize: '24px' }}>✅</span>
              </div>
              <div className="metric-content">
                <h3 className="metric-label">Approval Rate</h3>
                <p className="metric-value">{portfolioMetrics.applications.approval_rate.toFixed(2)}%</p>
                <span className="metric-detail">{portfolioMetrics.applications.approved} approved</span>
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-icon" style={{ backgroundColor: '#fce7f3' }}>
                <span style={{ color: '#ec4899', fontSize: '24px' }}>👥</span>
              </div>
              <div className="metric-content">
                <h3 className="metric-label">Total Customers</h3>
                <p className="metric-value">{portfolioMetrics.customers.total_customers}</p>
                <span className="metric-detail success">+{portfolioMetrics.customers.new_customers_30d} this month</span>
              </div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="charts-section">
            {/* Loan Disbursement Trend */}
            <div className="chart-card">
              <h2 className="chart-title">Loan Disbursement Trend (Last 12 Months)</h2>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={loanTrends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip
                      formatter={(value: any, name: string) => {
                        if (name === 'total_amount' || name === 'avg_amount') {
                          return formatCurrency(value);
                        }
                        return value;
                      }}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="loan_count" stroke="#2563eb" name="Loan Count" />
                    <Line type="monotone" dataKey="total_amount" stroke="#10b981" name="Total Amount" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="charts-row">
              {/* Loan Status Distribution */}
              <div className="chart-card half">
                <h2 className="chart-title">Loan Status Distribution</h2>
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={loanStatus}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry) => `${entry.status}: ${entry.count}`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="count"
                      >
                        {loanStatus.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Loan Amount by Status */}
              <div className="chart-card half">
                <h2 className="chart-title">Loan Amount by Status</h2>
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={loanStatus}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="status" />
                      <YAxis />
                      <Tooltip
                        formatter={(value: any) => formatCurrency(value)}
                      />
                      <Legend />
                      <Bar dataKey="total_amount" fill="#2563eb" name="Total Amount" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <style>{`
        .metrics-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 20px;
          margin-bottom: 32px;
        }

        .metric-card {
          background: white;
          border-radius: 8px;
          padding: 20px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          display: flex;
          gap: 16px;
          align-items: flex-start;
        }

        .metric-icon {
          width: 48px;
          height: 48px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .metric-content {
          flex: 1;
        }

        .metric-label {
          font-size: 14px;
          color: #666;
          margin: 0 0 4px 0;
          font-weight: 500;
        }

        .metric-value {
          font-size: 24px;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0 0 4px 0;
        }

        .metric-detail {
          font-size: 13px;
          color: #666;
        }

        .metric-detail.success {
          color: #10b981;
        }

        .metric-detail.warning {
          color: #f59e0b;
        }

        .metric-detail.error {
          color: #ef4444;
        }

        .charts-section {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .charts-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
        }

        .chart-card {
          background: white;
          border-radius: 8px;
          padding: 24px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .chart-card.half {
          width: 100%;
        }

        .chart-title {
          font-size: 18px;
          font-weight: 600;
          color: #1a1a1a;
          margin: 0 0 20px 0;
        }

        .chart-container {
          width: 100%;
        }

        @media (max-width: 968px) {
          .charts-row {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};
